import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { YEAR_QUERY_PARAM } from '../routes/routeConstants';

interface UseSelectedYearResult {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
}

// Parses the ?year= query param value; null when absent or not a valid year.
// NOTE: useYearPlan holds an identical private copy of this. Left alone here
// deliberately — that hook is the most intricate in the app and this is a design
// branch — but the two should be folded together next time it is touched.
function parseYearParam(value: string | null): number | null {
  if (!value) return null;
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
}

/**
 * The selected year, held in the URL rather than in component state.
 *
 * Same convention as the year plan: the year survives a refresh and travels with
 * a shared link, and navigating from the overview into a location's year or week
 * view carries it, which is what the 18 August decision asked for.
 */
export function useSelectedYear(): UseSelectedYearResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = parseYearParam(searchParams.get(YEAR_QUERY_PARAM)) ?? new Date().getFullYear();

  const setSelectedYear = useCallback(
    (year: number): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(YEAR_QUERY_PARAM, String(year));
        return next;
      });
    },
    [setSearchParams]
  );

  return { selectedYear, setSelectedYear };
}
